import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { cloneDeep, get } from 'lodash';
import { useQuery } from 'react-query';
import { MetricInfo } from '@pulse/metrics';
import metricsStore from '@globalStore/metricsStore';
import { getPreDefinedMetricsMeta, getUserDefinedMetricsMeta } from '@api/meta';
import { mergeObjectsWithSameKey } from '@utils/common';
import { updateAppAllMetrics } from '@store/common/actions';

function useMetricsMeta() {
  const dispatch = useDispatch();
  const setPreDefinedMetricsMeta = metricsStore((state) => state.setPreDefinedMetricsMeta);
  const setUserDefinedMetricsMeta = metricsStore((state) => state.setUserDefinedMetricsMeta);
  const setCombinedMetricsMeta = metricsStore((state) => state.setCombinedMetricsMeta);
  const setCombinedMetricsKey = metricsStore((state) => state.setCombinedMetricsKey);

  const { data: preDefinedMetrics, isLoading: isPreDefinedMetricsLoading } = useQuery(
    'pre-defined-metrics-meta',
    () => getPreDefinedMetricsMeta({}),
    {
      retry: true,
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnWindowFocus: false,
      select: (response) => get(response, ['data'], {}),
    },
  );

  const {
    data: userDefinedMetrics,
    isLoading: isUserDefinedMetricsLoading,
    refetch: reFetchUserDefinedMetricsMeta,
  } = useQuery(
    'user-defined-metrics-meta',
    () =>
      getUserDefinedMetricsMeta({
        forceUpdate: true,
      }),
    {
      retry: true,
      staleTime: 5 * 60 * 1000, // 5 min
      select: (response) => get(response, ['data'], {}),
    },
  );

  useEffect(() => {
    setUserDefinedMetricsMeta(userDefinedMetrics);
    const metricsMeta = mergeObjectsWithSameKey(preDefinedMetrics, userDefinedMetrics);
    if (metricsMeta && metricsMeta.app) {
      const metricsByCategory = cloneDeep(metricsMeta.app);
      const updatedActiveMetrics = Object.values(metricsByCategory)
        .filter((m: MetricInfo) => !(m.template && m.definitionStatus !== 'defined'))
        .map((m: MetricInfo) => m.name);
      dispatch(updateAppAllMetrics(updatedActiveMetrics));
    }
    setCombinedMetricsMeta(metricsMeta);
    const activeMetricsKeys = Object.keys(metricsMeta).reduce((acc: any, key: any) => {
      const metricsByCategory = metricsMeta[key];
      const activeMetrics = Object.values(metricsByCategory)
        .filter((metric: any) => metric.active)
        .map((metric: any) => metric.name);
      return {
        ...acc,
        [key]: activeMetrics,
      };
    }, {});
    setCombinedMetricsKey(activeMetricsKeys);
  }, [userDefinedMetrics]);

  useEffect(() => {
    setPreDefinedMetricsMeta(preDefinedMetrics);
  }, [preDefinedMetrics]);

  const forceUpdateUserDefinedMetricsMeta = () => {
    reFetchUserDefinedMetricsMeta();
  };

  return {
    forceUpdateUserDefinedMetricsMeta,
    isUserDefinedMetricsLoading,
    isPreDefinedMetricsLoading,
  };
}

export default useMetricsMeta;
